import api from './api';

export const invoicingService = {
  // Organization Invoice Settings
  getOrganizationSettings: async () => {
    const response = await api.get('/v1/invoicing/settings/');
    return response.data;
  },

  updateOrganizationSettings: async (settingsData) => {
    const response = await api.put('/v1/invoicing/settings/', settingsData);
    return response.data;
  },

  // Invoices
  getInvoices: async (params = {}) => {
    const response = await api.get('/v1/invoicing/invoices/', { params });
    return response.data;
  },

  getInvoice: async (id) => {
    const response = await api.get(`/v1/invoicing/invoices/${id}/`);
    return response.data;
  },

  createInvoice: async (invoiceData) => {
    const response = await api.post('/v1/invoicing/invoices/', invoiceData);
    return response.data;
  },

  updateInvoice: async (id, invoiceData) => {
    const response = await api.put(`/v1/invoicing/invoices/${id}/`, invoiceData);
    return response.data;
  },

  deleteInvoice: async (id) => {
    const response = await api.delete(`/v1/invoicing/invoices/${id}/`);
    return response.data;
  },

  markInvoicePaid: async (id, paymentDate = null) => {
    const response = await api.post(`/v1/invoicing/invoices/${id}/mark-paid/`, {
      payment_date: paymentDate,
    });
    return response.data;
  },

  // Download invoice PDF
  downloadInvoicePDF: async (id) => {
    const response = await api.get(`/v1/invoicing/invoices/${id}/pdf/`, {
      responseType: 'blob',
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `invoice_${id}.pdf`);
    link.setAttribute('target', '_blank'); // Also open in new tab
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return response.data;
  },

  // Export invoices to CSV
  exportInvoices: async (params = {}) => {
    const response = await api.get('/v1/invoicing/invoices/export/', {
      params,
      responseType: 'blob',
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `invoices_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return response.data;
  },

  // Export specific invoices by IDs
  exportSelectedInvoices: async (invoiceIds) => {
    const response = await api.get('/v1/invoicing/invoices/export/', {
      params: { ids: invoiceIds.join(',') },
      responseType: 'blob',
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `invoices_selected_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return response.data;
  },

  // Full update with line items
  updateInvoiceFull: async (id, invoiceData) => {
    const response = await api.put(`/v1/invoicing/invoices/${id}/update-full/`, invoiceData);
    return response.data;
  },

  // Regenerate PDF
  regeneratePDF: async (id) => {
    const response = await api.post(`/v1/invoicing/invoices/${id}/regenerate-pdf/`);
    return response.data;
  },

  // Resend email
  resendEmail: async (id) => {
    const response = await api.post(`/v1/invoicing/invoices/${id}/resend-email/`);
    return response.data;
  },

  // Edit and resend (combined)
  editAndResend: async (id, invoiceData) => {
    const response = await api.post(`/v1/invoicing/invoices/${id}/edit-and-resend/`, invoiceData);
    return response.data;
  },

  // Search bookings by VRN for invoice generation
  searchBookingsByVRN: async (vrn) => {
    const response = await api.get('/v1/invoicing/bookings-by-vrn/', {
      params: { vrn },
    });
    return response.data;
  },

  // Generate invoice from booking
  generateInvoiceFromBooking: async (bookingId, options = {}) => {
    const response = await api.post('/v1/invoicing/generate-from-booking/', {
      booking_id: bookingId,
      regenerate: options.regenerate || false,
      send_email: options.sendEmail !== undefined ? options.sendEmail : true,
    });
    return response.data;
  },
};
